import React from 'react';
import { Award, Globe, Users, TrendingUp } from 'lucide-react';

import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const About = () => {
  const { language } = useLanguage();
  const t = translations[language].about;

  const statsIcons = [
    <Award className="h-8 w-8" />,
    <Globe className="h-8 w-8" />,
    <Users className="h-8 w-8" />,
    <TrendingUp className="h-8 w-8" />
  ];

  return (
    <div className="min-h-screen bg-white pt-[120px]">
      {/* Hero Section */}
      <div className="relative h-[70vh] bg-gradient-to-r from-gray-900 to-red-900">
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative h-full flex items-center justify-center text-center">
          <div className="max-w-4xl px-4">
            <h1 className="text-6xl md:text-7xl font-bold text-white mb-6">{t.title}</h1>
            <p className="text-2xl text-white/90">
              {t.subtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {t.stats.map((stat, index) => (
            <div
              key={index}
              className="text-center p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className="text-red-600 flex justify-center mb-4">{statsIcons[index]}</div>
              <div className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
              <div className="text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Mission & Vision */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-white p-10 rounded-2xl shadow-lg">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">{t.missionTitle}</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                {t.missionText}
              </p>
            </div>
            <div className="bg-white p-10 rounded-2xl shadow-lg">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">{t.visionTitle}</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                {t.visionText}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-4xl font-bold text-center text-gray-900 mb-16">{t.timelineTitle}</h2>
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-red-600 hidden lg:block" />

          <div className="space-y-12">
            {t.timeline.map((item, index) => (
              <div
                key={index}
                className={`flex flex-col lg:flex-row items-center gap-8 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  }`}
              >
                <div className="flex-1 text-center lg:text-right">
                  <div
                    className={`bg-white p-6 rounded-2xl shadow-lg inline-block ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'
                      }`}
                  >
                    <div className="text-3xl font-bold text-red-600 mb-2">{item.year}</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{item.event}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
                <div className="relative z-10">
                  <div className="w-6 h-6 bg-red-600 rounded-full border-4 border-white shadow-lg" />
                </div>
                <div className="flex-1" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;